import moment from 'moment';
import t, { partial } from '../../util/translation/translation';
import { formatAmount } from '../transactions/connect';

export const p = partial('accounting');

export const STATUS_COLORS = {
  draft: 'secondary',
  approved: 'warning',
  paid: 'success',
  invoiced: 'info',
};

export const STATE_COLORS = {
  paid_in_app: 'success',
  paid_outside: 'primary',
  no_sale: 'secondary',
  unpaid: 'warning',
};

/** Last month ("2026-10"): the one usually being settled */
export const lastMonth = () => moment().subtract(1, 'month').format('YYYY-MM');

/** The most months shown at once: each is its own request */
export const MAX_MONTHS = 12;

/** The month `count` months later (or earlier, when negative) */
export const shiftMonth = (period, count) =>
  moment(period, 'YYYY-MM').add(count, 'month').format('YYYY-MM');

/** The months from `from` to `to` ("2026-08".."2026-10"), oldest first */
export const monthsBetween = (from, to) => {
  const months = [];
  const month = moment(from, 'YYYY-MM');
  const last = moment(to, 'YYYY-MM');
  while (!month.isAfter(last) && months.length < MAX_MONTHS) {
    months.push(month.format('YYYY-MM'));
    month.add(1, 'month');
  }
  return months;
};

/** Whether the month has ended, so it can be approved */
export const isMonthOver = (period) => period < moment().format('YYYY-MM');

/** "2026-08", or "2026-08 – 2026-10" for a range */
export const rangeText = (periods) => {
  const first = periods[0];
  const last = periods[periods.length - 1];
  return first === last ? first : `${first} – ${last}`;
};

/**
 * The server's reason in words (its code, e.g. accounting.RateMissing, has a
 * text of its own), else `fallback`
 */
export const errorText = (err, fallback) => {
  if (typeof err === 'string' && err) {
    return err;
  }
  const code = err?.data?.message;
  if (typeof code === 'string' && code.includes('.')) {
    try {
      const text = t(code);
      if (text && !text.startsWith('Missing translation')) {
        return text;
      }
    } catch {
      // No text for it
    }
  }
  return p(fallback);
};

/** Pay out to the club (positive) or invoice it (negative) */
export const balanceText = (balance, currency) =>
  balance < 0
    ? `${p('invoice')} ${formatAmount(-balance, currency)}`
    : `${p('payOut')} ${formatAmount(balance, currency)}`;

/** A month's numbers flat, to be added up */
export const valuesOf = (summary) => ({
  sales: summary.sales,
  sold: summary.sold,
  paidInApp: summary.paidInApp.amount,
  connect: summary.paidInApp.connect,
  paidOutside: summary.paidOutside.amount,
  unpaid: summary.unpaid.amount,
  noSale: summary.noSale.amount,
  held: summary.held,
  cutOwed: summary.cutOwed,
  cutCollected: summary.cutCollected,
  adjustments: summary.adjustments,
  balance: summary.balance,
  missingRate: summary.missingRate || 0,
  // Payment fees (Stripe, Swish), only shown: one number per currency (the platform
  // account's), so they add up like the rest
  paymentFeeMissing: summary.paymentFeeMissing || 0,
  ...Object.fromEntries(
    (summary.paymentFees || []).map((fee) => [
      `paymentFee:${fee.currency}`,
      fee.amount,
    ])
  ),
});

/** Payment fees in flat numbers, as text: "123,45 SEK" (several: joined) */
export const paymentFeeText = (values) => {
  const fees = Object.keys(values)
    .filter((key) => key.startsWith('paymentFee:'))
    .map((key) => formatAmount(values[key], key.slice('paymentFee:'.length)));
  return fees.length ? fees.join(' · ') : '–';
};

/** Flat numbers added up */
export const addValues = (a, b) =>
  Object.keys(b).reduce(
    (sum, key) => ({ ...sum, [key]: (a[key] || 0) + b[key] }),
    {}
  );

/**
 * Each club over the months: its numbers added up, and each month's status
 * (months is one getMonth answer per period)
 */
export const clubsOverMonths = (periods, months) => {
  const clubs = new Map();
  months.forEach((rows, index) => {
    (rows || []).forEach((row) => {
      const key = `${row.teamId}`;
      const club = clubs.get(key) || {
        teamId: row.teamId,
        clubName: row.clubName,
        currency: row.currency,
        values: {},
        months: [],
      };
      club.values = addValues(club.values, valuesOf(row.summary));
      club.months.push({ period: periods[index], status: row.status });
      if (!club.currency) {
        club.currency = row.currency;
      }
      clubs.set(key, club);
    });
  });
  return [...clubs.values()];
};

/** Every club's numbers added up, per currency */
export const totalsByCurrency = (clubs) => {
  const totals = {};
  clubs.forEach((club) => {
    totals[club.currency] = {
      currency: club.currency,
      ...addValues(totals[club.currency] || {}, club.values),
    };
  });
  return Object.values(totals);
};
